import React, { useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';

interface CuOPowderProps {
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: number;
  /** Total mass in grams — used to size the pile. */
  massG?: number;
  /** Maximum mass the pile is sized against (default 2.4 g). */
  capacityG?: number;
  /** 0 = pure black CuO, 1 = fully reduced copper-brown Cu. */
  reductionProgress?: number;
  /** Optional cylinder constraint — radius of the glass tube the pile sits inside. */
  containerRadius?: number;
}

/**
 * Copper(II) oxide powder pile — black powder that progressively turns
 * reddish-brown copper as it is reduced by hydrogen gas. Used inside the
 * horizontal glass tube during Activity 3.13.
 *
 * The colour is lerped per frame between black (#1a1a1a) and copper
 * (#b87333) by `reductionProgress`. The pile is shaped as a small
 * ellipsoidal heap inside the tube's diameter so it visually fits.
 */
export function CuOPowder({
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  scale = 1,
  massG = 0,
  capacityG = 2.4,
  reductionProgress = 0,
  containerRadius = 0.025,
}: CuOPowderProps) {
  const matRef = useRef<THREE.MeshStandardMaterial>(null);

  useFrame(() => {
    if (matRef.current) {
      const t = Math.max(0, Math.min(1, reductionProgress));
      // Lerp between deep black (CuO) and copper-brown (Cu)
      const r = 0.10 + (0.72 - 0.10) * t;
      const g = 0.10 + (0.45 - 0.10) * t;
      const b = 0.10 + (0.20 - 0.10) * t;
      matRef.current.color.setRGB(r, g, b);
      // Copper has higher metallic sheen than the dull black oxide
      matRef.current.metalness = 0.05 + 0.55 * t;
      matRef.current.roughness = 0.95 - 0.45 * t;
    }
  });

  const fraction = Math.max(0, Math.min(1, massG / Math.max(0.0001, capacityG)));
  // Pile width is bounded by the container's inner radius
  const pileWidth = Math.min(containerRadius * 1.6, 0.018 + 0.026 * fraction);
  const pileLength = Math.min(0.06, 0.018 + 0.036 * fraction);
  const pileHeight = 0.006 + 0.012 * fraction;

  // Deterministic dusting particles around the pile for texture
  const dusts = useMemo(() => {
    const arr: { x: number; z: number; r: number }[] = [];
    let s = 211;
    const rand = () => {
      s = (s * 9301 + 49297) % 233280;
      return s / 233280;
    };
    for (let i = 0; i < 16; i++) {
      arr.push({
        x: (rand() - 0.5) * pileLength * 0.9,
        z: (rand() - 0.5) * pileWidth * 0.9,
        r: 0.0012 + rand() * 0.0014,
      });
    }
    return arr;
  }, [pileLength, pileWidth]);

  if (massG <= 0.0001) return null;

  return (
    <group position={position} rotation={rotation} scale={scale}>
      {/* Main heap — unit sphere scaled into a long flat ellipsoid that
          fits inside the glass tube. */}
      <mesh
        position={[0, pileHeight / 2, 0]}
        scale={[pileLength / 2, pileHeight / 2, pileWidth / 2]}
        castShadow
      >
        <sphereGeometry args={[1, 22, 18]} />
        <meshStandardMaterial
          ref={matRef}
          color="#1a1a1a"
          roughness={0.95}
          metalness={0.05}
        />
      </mesh>
      {/* Dust speckles around the pile base */}
      {dusts.map((d, i) => (
        <mesh key={i} position={[d.x, 0.0005, d.z]}>
          <sphereGeometry args={[d.r, 6, 6]} />
          <meshStandardMaterial
            color={reductionProgress > 0.5 ? '#b87333' : '#1a1a1a'}
            roughness={0.95}
          />
        </mesh>
      ))}
    </group>
  );
}

export default CuOPowder;
